import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  User,
  Download,
  Check,
  Loader2,
  CheckSquare,
  Square,
} from "lucide-react";
import logo1 from "../../assets/Image (3).jpg";
import FeedbackComponent from "../../component/pages/Falcon360/targetMapping/FeedbackComponent";

// LoadingSpinner component moved outside to avoid re-creation on each render
const LoadingSpinner = () => (
  <svg
    className="animate-spin h-3 w-3"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// Helper function to get status badge class
const getStatusBadgeClass = (displayValue) => {
  if (
    displayValue.includes("✅") ||
    displayValue.toLowerCase().includes("mapped")
  ) {
    return "bg-green-100 text-green-800";
  }
  if (
    displayValue.includes("❌") ||
    displayValue.toLowerCase().includes("not mapped")
  ) {
    return "bg-red-100 text-red-800";
  }
  if (
    displayValue.includes("⚠️") ||
    displayValue.toLowerCase().includes("partial")
  ) {
    return "bg-yellow-100 text-yellow-800";
  }
  return "bg-gray-100 text-gray-800";
};

// Helper function to get mandatory badge class
const getMandatoryBadgeClass = (displayValue) => {
  const lowerValue = displayValue.toLowerCase();
  return lowerValue === "yes" || lowerValue === "true"
    ? "bg-red-100 text-red-800"
    : "bg-gray-100 text-gray-800";
};

// Helper function to get cell content class
const getCellContentClass = (key) => {
  const lowerKey = key.toLowerCase();
  return lowerKey.includes("column") ||
    lowerKey.includes("table") ||
    lowerKey.includes("name")
    ? "font-medium"
    : "";
};

// Component to render approve button
const ApproveButton = ({
  msg,
  selectedRows,
  isApproving,
  handleApproveSelected,
}) => {
  // Helper function to extract table data
  const extractTableData = () => {
    if (Array.isArray(msg.data)) {
      return msg.data;
    }
    if (msg.data && typeof msg.data === "object") {
      const arrayKeys = Object.keys(msg.data).filter(
        (key) => Array.isArray(msg.data[key]) && msg.data[key].length > 0,
      );
      if (arrayKeys.length > 0) {
        return msg.data[arrayKeys[0]];
      }
    }
    return null;
  };

  // Helper function to get selected indices
  const getSelectedIndices = (tableData) => {
    const indices = [];
    tableData.forEach((_, index) => {
      if (selectedRows[`${msg.id}-${index}`]) {
        indices.push(index);
      }
    });
    return indices;
  };

  // Helper function to handle approve click
  const handleApproveClick = (tableData, indices) => {
    const selectedRowsData = indices.map((i) => tableData[i]);
    handleApproveSelected(msg.id, selectedRowsData, msg.mappingFileId);
  };

  const actualTableData = extractTableData();
  if (
    !actualTableData ||
    !Array.isArray(actualTableData) ||
    actualTableData.length === 0
  ) {
    return null;
  }

  const selectedIndices = getSelectedIndices(actualTableData);
  if (selectedIndices.length === 0) {
    return null;
  }

  const buttonClassName = isApproving
    ? "bg-green-400 cursor-not-allowed"
    : "bg-green-500 hover:bg-green-600";

  return (
    <button
      onClick={() => handleApproveClick(actualTableData, selectedIndices)}
      disabled={isApproving}
      className={`flex items-center gap-1 px-3 py-1 text-xs text-white rounded-md transition-all ${buttonClassName}`}
    >
      {isApproving ? (
        <>
          <LoadingSpinner />
          <span>Approving...</span>
        </>
      ) : (
        <>
          <Check className="w-3 h-3" />
          <span>Approve Selected ({selectedIndices.length})</span>
        </>
      )}
    </button>
  );
};

ApproveButton.propTypes = {
  msg: PropTypes.object.isRequired,
  selectedRows: PropTypes.object.isRequired,
  isApproving: PropTypes.bool.isRequired,
  handleApproveSelected: PropTypes.func.isRequired,
};

// AFTER
// Remove the hard block on missing mappingFileId
// and add a fallback for table name field variations

// Component to render reject button
const RejectButton = ({
  msg,
  selectedRows,
  isRejecting,
  handleRejectSelected,
}) => {
  const extractTableData = () => {
    if (Array.isArray(msg.data)) return msg.data;
    if (msg.data && typeof msg.data === "object") {
      const arrayKeys = Object.keys(msg.data).filter(
        (key) => Array.isArray(msg.data[key]) && msg.data[key].length > 0,
      );
      if (arrayKeys.length > 0) return msg.data[arrayKeys[0]];
    }
    return null;
  };

  const actualTableData = extractTableData();
  if (
    !actualTableData ||
    !Array.isArray(actualTableData) ||
    actualTableData.length === 0
  )
    return null;

  const selectedIndices = actualTableData
    .map((_, index) => (selectedRows[`${msg.id}-${index}`] ? index : null))
    .filter((i) => i !== null);

  if (selectedIndices.length === 0) return null;

  const selectedRowsData = selectedIndices.map((i) => actualTableData[i]);

  return (
    <button
      onClick={() =>
        handleRejectSelected(msg.id, selectedRowsData, msg.mappingFileId)
      }
      disabled={isRejecting}
      className={`flex items-center gap-1 px-3 py-1 text-xs text-white rounded-md transition-all ${
        isRejecting
          ? "bg-red-300 cursor-not-allowed"
          : "bg-red-500 hover:bg-red-600"
      }`}
    >
      {isRejecting ? (
        <>
          <LoadingSpinner />
          <span>Rejecting...</span>
        </>
      ) : (
        <>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <span>Reject Selected ({selectedIndices.length})</span>
        </>
      )}
    </button>
  );
};

RejectButton.propTypes = {
  msg: PropTypes.object.isRequired,
  selectedRows: PropTypes.object.isRequired,
  isRejecting: PropTypes.bool.isRequired,
  handleRejectSelected: PropTypes.func.isRequired,
};

const PreviewButton = ({ msg, selectedRows, handlePreview }) => {
  const extractTableData = () => {
    if (Array.isArray(msg.data)) return msg.data;
    if (msg.data && typeof msg.data === "object") {
      const arrayKeys = Object.keys(msg.data).filter(
        (key) => Array.isArray(msg.data[key]) && msg.data[key].length > 0,
      );
      if (arrayKeys.length > 0) return msg.data[arrayKeys[0]];
    }
    return null;
  };

  // ✅ Dynamically detect the source/target table name field
  // Excludes sap_table_name — picks the first *_table_name that isn't SAP
  const detectTableNameField = (row) => {
    if (!row || typeof row !== "object") return null;

    const keys = Object.keys(row);

    // Priority: look for known non-SAP table name fields dynamically
    const tableNameKey = keys.find((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes("table") &&
        lower.includes("name") &&
        !lower.startsWith("sap") // exclude sap_table_name
      );
    });

    return tableNameKey ? row[tableNameKey] : null;
  };

  const tableData = extractTableData();
  if (!tableData || !Array.isArray(tableData) || tableData.length === 0)
    return null;

  const hasSelectedRows = tableData.some(
    (_, index) => selectedRows[`${msg.id}-${index}`],
  );
  if (!hasSelectedRows) return null;

  const distinctTableNames = [
    ...new Set(
      tableData
        .filter((_, index) => selectedRows[`${msg.id}-${index}`])
        .map((row) => detectTableNameField(row))
        .filter(Boolean),
    ),
  ];

  if (distinctTableNames.length === 0) return null;

  return (
    <button
      onClick={() =>
        handlePreview(
          distinctTableNames,
          msg.mappingFileId,
          Array.isArray(msg.data) ? msg.data : [],
        )
      }
      className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-all"
      title={`Preview ${distinctTableNames.length} table(s)`}
    >
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
      <span>
        Preview
        {distinctTableNames.length > 1 ? ` (${distinctTableNames.length})` : ""}
      </span>
    </button>
  );
};

PreviewButton.propTypes = {
  msg: PropTypes.object.isRequired,
  selectedRows: PropTypes.object.isRequired,
  handlePreview: PropTypes.func.isRequired,
};

/// Component to render table cell content
const TableCellContent = ({ columnKey, displayValue }) => {
  const lowerKey = columnKey.toLowerCase();

  if (lowerKey.includes("status")) {
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(displayValue)}`}
      >
        {displayValue}
      </span>
    );
  }

  if (lowerKey.includes("mandatory")) {
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMandatoryBadgeClass(displayValue)}`}
      >
        {displayValue}
      </span>
    );
  }

  // Handle confidence as percentage with color coding
  if (lowerKey.includes("confidence")) {
    const confidenceValue = Number.parseFloat(displayValue);
    if (!Number.isNaN(confidenceValue)) {
      const percentage = (confidenceValue * 100).toFixed(1);
      const confidencePercent = Number.parseFloat(percentage);

      // Determine badge color based on confidence level
      const badgeClass =
        confidencePercent >= 80
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800";

      return (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}
        >
          {percentage}%
        </span>
      );
    }
  }

  return <span className={getCellContentClass(columnKey)}>{displayValue}</span>;
};

TableCellContent.propTypes = {
  columnKey: PropTypes.string.isRequired,
  displayValue: PropTypes.string.isRequired,
};

// Helper function to capitalize word
const capitalizeWord = (word) => word.charAt(0).toUpperCase() + word.slice(1);

// Helper function to format table name
const formatTableName = (key) => {
  return key.replaceAll("_", " ").replaceAll(/\b\w/g, (l) => l.toUpperCase());
};

// Helper function to format snake case
const formatSnakeCase = (key) => {
  return key.split("_").map(capitalizeWord).join(" ");
};

// Helper function to process array data
const processArrayData = (data, displayTableName) => {
  if (data.length > 0 && typeof data[0] === "object" && data[0] !== null) {
    return { tableData: data, displayTableName: displayTableName || "Results" };
  }
  const tableData = data.map((item, index) => ({
    "Sr.No": index + 1,
    Value: item,
  }));
  return { tableData, displayTableName: displayTableName || "Results" };
};

// Helper function to check if object is single message
const isSingleMessage = (data, key) => {
  return (
    data[key] && typeof data[key] === "string" && Object.keys(data).length === 1
  );
};

// Helper function to process object data
const processObjectData = (data, displayTableName) => {
  if (isSingleMessage(data, "message") || isSingleMessage(data, "detail")) {
    return null;
  }

  // SPECIAL HANDLING FOR common_columns STRUCTURE
  if (data.common_columns && Array.isArray(data.common_columns)) {
    const transformedData = data.common_columns.map((item, index) => ({
      "Sr.No": index + 1,
      table_a: item.table_a || "",
      table_b: item.table_b || "",
      common_columns: Array.isArray(item.common_columns)
        ? item.common_columns.join(", ")
        : item.common_columns || "",
    }));

    return {
      tableData: transformedData,
      displayTableName: displayTableName || "Common Columns",
    };
  }

  const arrayKeys = Object.keys(data).filter(
    (key) => Array.isArray(data[key]) && data[key].length > 0,
  );

  if (arrayKeys.length > 0) {
    const firstArrayKey = arrayKeys[0];
    const firstArray = data[firstArrayKey];

    if (
      firstArray[0] &&
      typeof firstArray[0] === "object" &&
      firstArray[0] !== null
    ) {
      return {
        tableData: firstArray,
        displayTableName: displayTableName || formatTableName(firstArrayKey),
      };
    }

    const tableData = firstArray.map((item, index) => ({
      "Sr.No": index + 1,
      [formatTableName(firstArrayKey)]: item,
    }));
    return {
      tableData,
      displayTableName: displayTableName || formatTableName(firstArrayKey),
    };
  }

  const objectKeys = Object.keys(data);
  if (objectKeys.length > 0) {
    const tableData = objectKeys.map((key) => ({
      Property: formatTableName(key),
      Value:
        typeof data[key] === "object" && data[key] !== null
          ? JSON.stringify(data[key])
          : String(data[key] || ""),
    }));
    return { tableData, displayTableName: displayTableName || "Details" };
  }

  return null;
};

// Helper function to get column header text
const getColumnHeader = (key) => {
  const headerMappings = {
    sap_object_name: "SAP Object Name",
    sap_table_name: "SAP Table Name",
    sap_column_name: "SAP Column Name",
    sap_description: "SAP Description",
    sap_mandatory: "SAP Mandatory",
    ax_table_name: "AX Table Name",
    ax_field_name: "AX Field Name",
    ax_business_meaning: "AX Business Meaning",
    overall_confidence: "Overall Confidence",
    mapping_status: "Mapping Status",
    organization_config_id: "Organization Config ID",
    mapping_file_id: "Mapping File ID",
    SAPTableName: "SAP Table Name",
    SAPColumnName: "SAP Column Name",
    AXTableName: "AX Table Name",
    AXColumnName: "AX Column Name",
    ObjectName: "Object Name",
    Description: "Description",
    Mandatory: "Mandatory",
    index: "Sr.No",
    table_a: "Table A",
    table_b: "Table B",
    common_columns: "Common Columns",
  };

  if (headerMappings[key]) {
    return headerMappings[key];
  }

  if (key.includes("_")) {
    return formatSnakeCase(key);
  }

  return key.replaceAll(/([a-z])([A-Z])/g, "$1 $2");
};

const ChatMessages = ({
  currentChat,
  loadingMessages,
  isTyping,
  selectedRows = {},
  handleRowSelect,
  handleSelectAll,
  isApproving,
  regeneratingMessageId,
  handleFeedbackSubmit,
  handleRegenerate,
  handleDownload,
  handleApproveSelected,
  handlePreview,
  handleRejectSelected,
  isRejecting,
  messagesEndRef,
}) => {
  const [visibleRows, setVisibleRows] = useState({});
  const INITIAL_ROW_LIMIT = 10;

  const handleLoadMore = (messageId) => {
    setVisibleRows((prev) => ({
      ...prev,
      [messageId]: true,
    }));
  };
  const renderTableFromJSON = (data, messageId) => {
    try {
      if (!data) {
        return null;
      }

      // Check if data contains both ax_tables and sap_tables
      if (data.ax_tables && data.sap_tables) {
        return (
          <div className="mt-3 px-4 w-full">
            <div className="flex gap-4 w-full">
              <div className="flex-1 min-w-0">
                {renderSingleTable(
                  data.ax_tables,
                  messageId,
                  "AX Tables",
                  true,
                )}
              </div>
              <div className="flex-1 min-w-0">
                {renderSingleTable(
                  data.sap_tables,
                  messageId,
                  "SAP Tables",
                  true,
                )}
              </div>
            </div>
          </div>
        );
      }

      // Otherwise render as single table
      return renderSingleTable(data, messageId, null, false);
    } catch (error) {
      console.error("Error in renderTableFromJSON:", error);
      return (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            Error rendering table data: {error.message}
          </p>
        </div>
      );
    }
  };

  const renderSingleTable = (
    data,
    messageId,
    tableName = null,
    isHorizontalLayout = false,
  ) => {
    try {
      if (!data) {
        return null;
      }

      let tableData = null;
      let displayTableName = tableName || "";

      if (Array.isArray(data)) {
        const result = processArrayData(data, displayTableName);
        tableData = result.tableData;
        displayTableName = result.displayTableName;
      } else if (typeof data === "object" && data !== null) {
        const result = processObjectData(data, displayTableName);
        if (!result) {
          return null;
        }
        tableData = result.tableData;
        displayTableName = result.displayTableName;
      }

      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        return null;
      }

      const validTableData = tableData.filter(
        (item) => item && typeof item === "object" && item !== null,
      );

      if (validTableData.length === 0) {
        return null;
      }

      // Define columns to hide
      // Define columns to hide
      const columnsToHide = new Set([
        "id",
        "mapping_status",
        "organization_config_id",
        "mapping_file_id",
        "ax_business_meaning",
        "source_table_name",
        "source_field_name",
      ]);

      // Helper: determine if a row has a pending approval mismatch
      const isPendingApproval = (row) => {
        const sourceTable = (row.source_table_name || "").trim().toLowerCase();
        const sourceField = (row.source_field_name || "").trim().toLowerCase();
        const axTable = (row.ax_table_name || "").trim().toLowerCase();
        const axField = (row.ax_field_name || "").trim().toLowerCase();

        // If source fields are empty, nothing to compare → not pending
        if (!sourceTable && !sourceField) return false;

        // Pending if either table or field doesn't match
        return sourceTable !== axTable || sourceField !== axField;
      };

      // CHECK IF TABLE HAS MAPPING STATUS COLUMN (check BEFORE filtering)
      const hasMappingStatus =
        validTableData.length > 0 &&
        validTableData[0].hasOwnProperty("mapping_status");

      // Get all keys and filter out hidden columns
      const allKeys = [
        ...new Set(validTableData.flatMap((item) => Object.keys(item))),
      ].filter((key) => !columnsToHide.has(key));

      return (
        <div className={`${isHorizontalLayout ? "" : "mt-3 px-4"} w-full`}>
          {displayTableName && (
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {displayTableName}
            </h4>
          )}
          <div className="block w-full overflow-x-auto">
            <table className="w-full bg-white border border-gray-200 rounded-lg text-xs table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {/* ONLY SHOW CHECKBOX COLUMN IF MAPPING STATUS EXISTS */}
                  {hasMappingStatus && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b w-12">
                      <button
                        onClick={() =>
                          handleSelectAll(messageId, validTableData.length)
                        }
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                        title="Select all"
                      >
                        {Array.from({ length: validTableData.length }).every(
                          (_, i) => selectedRows[`${messageId}-${i}`],
                        ) ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                  )}
                  {allKeys.map((key) => (
                    <th
                      key={key}
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                      style={{ width: `${100 / (allKeys.length + 1)}%` }}
                    >
                      {getColumnHeader(key)}
                    </th>
                  ))}
                  {hasMappingStatus && (
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                      style={{ width: `${100 / (allKeys.length + 1)}%` }}
                    >
                      Pending Approval
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {validTableData
                  .slice(
                    0,
                    visibleRows[messageId]
                      ? validTableData.length
                      : INITIAL_ROW_LIMIT,
                  )
                  .map((item, rowIndex) => {
                    const rowKey = `${messageId}-${rowIndex}`;
                    const isSelected = selectedRows[rowKey];
                    // Generate stable key from row data
                    const stableRowKey =
                      item.id || item.Index || `${messageId}-row-${rowIndex}`;
                    return (
                      <tr
                        key={stableRowKey}
                        className={`hover:bg-gray-50 ${
                          isSelected ? "bg-blue-50" : ""
                        }`}
                      >
                        {/* ONLY SHOW CHECKBOX IF MAPPING STATUS EXISTS */}
                        {hasMappingStatus && (
                          <td className="px-3 py-2 text-xs border-b w-12">
                            {isPendingApproval(item) ? (
                              <button
                                onClick={() =>
                                  handleRowSelect(messageId, rowIndex)
                                }
                                className="text-gray-600 hover:text-blue-600 transition-colors"
                              >
                                {isSelected ? (
                                  <CheckSquare className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <Square className="w-4 h-4" />
                                )}
                              </button>
                            ) : (
                              <Square className="w-4 h-4 text-gray-200 cursor-not-allowed" />
                            )}
                          </td>
                        )}
                        {allKeys.map((key) => {
                          const value = item[key] || "";
                          let displayValue =
                            typeof value === "object" && value !== null
                              ? JSON.stringify(value)
                              : String(value);

                          // Special handling for common_columns
                          if (key === "common_columns" && displayValue) {
                            const columns = displayValue.split(", ");
                            if (columns.length > 5) {
                              const visibleColumns = columns
                                .slice(0, 5)
                                .join(", ");
                              const hiddenCount = columns.length - 5;
                              displayValue = (
                                <div className="flex flex-col gap-1">
                                  <span className="short-columns text-gray-900 font-medium">
                                    {visibleColumns}
                                  </span>
                                  <span className="hidden full-columns text-gray-900 font-medium">
                                    {displayValue}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const cell =
                                        e.currentTarget.closest("td");
                                      const fullText =
                                        cell.querySelector(".full-columns");
                                      const shortText =
                                        cell.querySelector(".short-columns");
                                      const button = e.currentTarget;

                                      if (fullText && shortText) {
                                        fullText.classList.toggle("hidden");
                                        shortText.classList.toggle("hidden");

                                        // Update button text based on state
                                        if (
                                          fullText.classList.contains("hidden")
                                        ) {
                                          button.textContent = `Show more (${hiddenCount} more)`;
                                        } else {
                                          button.textContent = "Show less";
                                        }
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-left text-xs font-medium"
                                  >
                                    Show more ({hiddenCount} more)
                                  </button>

                                  <span className="hidden full-columns text-gray-900 font-medium">
                                    {displayValue}
                                  </span>
                                </div>
                              );
                            }
                          }

                          return (
                            <td
                              key={`${stableRowKey}-${key}`}
                              className="px-2 py-2 text-xs text-gray-900 border-b break-words"
                              title={
                                typeof displayValue === "string"
                                  ? displayValue
                                  : ""
                              }
                            >
                              {key === "common_columns" &&
                              typeof displayValue !== "string" ? (
                                displayValue
                              ) : (
                                <TableCellContent
                                  columnKey={key}
                                  displayValue={displayValue}
                                />
                              )}
                            </td>
                          );
                        })}
                        {hasMappingStatus && (
                          <td className="px-2 py-2 text-xs border-b">
                            {isPendingApproval(item) ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                                Yes
                              </span>
                            ) : null}
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {validTableData.length > INITIAL_ROW_LIMIT &&
            !visibleRows[messageId] && (
              <div className="mt-1 text-center">
                <button
                  onClick={() => handleLoadMore(messageId)}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                >
                  Show more... ({validTableData.length - INITIAL_ROW_LIMIT} more
                  rows)
                </button>
              </div>
            )}
        </div>
      );
    } catch (error) {
      console.error("Error in renderSingleTable:", error);
      return (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            Error rendering table data: {error.message}
          </p>
        </div>
      );
    }
  };

  return (
    <div className="flex-1 p-4 space-y-4 overflow-y-auto min-h-0">
      {(() => {
        if (loadingMessages) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading messages...</p>
              </div>
            </div>
          );
        }
        if (currentChat?.messages && currentChat.messages.length > 0) {
          return (
            <>
              {currentChat.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {msg.sender === "ai" && (
                    <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                      <img
                        src={logo1}
                        alt="AI Logo"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div
                    className={(() => {
                      if (msg.sender === "user") {
                        return "max-w-3xl";
                      }
                      return msg.data ? "w-full max-w-full" : "max-w-5xl";
                    })()}
                  >
                    <div
                      className={(() => {
                        if (msg.sender === "user") {
                          return "py-2 rounded-lg px-4 bg-blue-500 text-white";
                        }
                        if (msg.data) {
                          return "py-2 rounded-lg px-0 bg-white border border-gray-200 text-gray-800 w-full";
                        }
                        return "py-2 rounded-lg px-4 bg-white border border-gray-200 text-gray-800";
                      })()}
                    >
                      <p
                        className={`whitespace-pre-wrap ${
                          msg.data ? "px-4" : ""
                        }`}
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {msg.text}
                      </p>
                      {msg.sender === "ai" &&
                        msg.data &&
                        renderTableFromJSON(msg.data, msg.id)}

                      {msg.timestamp &&
                        !Number.isNaN(msg.timestamp.getTime()) && (
                          <span
                            className={`text-xs opacity-70 mt-1 block ${
                              msg.data ? "px-4" : ""
                            }`}
                          >
                            {msg.timestamp.toLocaleTimeString()}
                          </span>
                        )}
                    </div>
                    {msg.sender === "ai" &&
                      !msg.text.includes("I am your Mapping Assistant") && (
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                          <FeedbackComponent
                            messageId={msg.id}
                            onFeedbackSubmit={handleFeedbackSubmit}
                          />

                          {/* <button
                          onClick={() => handleRegenerate(msg.id)}
                          disabled={regeneratingMessageId === msg.id}
                          className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-all ${regeneratingMessageId === msg.id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          title="Regenerate response"
                        >
                          {regeneratingMessageId === msg.id ? (
                            <LoadingSpinner />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                        </button> */}

                          {msg.hasDownload && (
                            <button
                              onClick={() => handleDownload(msg, "csv")}
                              className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all"
                              title="Download CSV"
                            >
                              <Download className="w-3 h-4" />
                            </button>
                          )}

                          <PreviewButton
                            msg={msg}
                            selectedRows={selectedRows}
                            handlePreview={handlePreview}
                          />
                          <RejectButton
                            msg={msg}
                            selectedRows={selectedRows}
                            isRejecting={isRejecting}
                            handleRejectSelected={handleRejectSelected}
                          />

                          <ApproveButton
                            msg={msg}
                            selectedRows={selectedRows}
                            isApproving={isApproving}
                            handleApproveSelected={handleApproveSelected}
                          />
                        </div>
                      )}
                  </div>
                  {msg.sender === "user" && (
                    <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                    <img
                      src={logo1}
                      alt="AI Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          );
        }
        return (
          <div className="flex items-center justify-center h-full text-gray-400">
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        );
      })()}
    </div>
  );
};

ChatMessages.propTypes = {
  currentChat: PropTypes.object,
  loadingMessages: PropTypes.bool,
  isTyping: PropTypes.bool,
  selectedRows: PropTypes.object,
  handleRowSelect: PropTypes.func.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
  isApproving: PropTypes.bool,
  regeneratingMessageId: PropTypes.string,
  handleFeedbackSubmit: PropTypes.func.isRequired,
  handleRegenerate: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  handleApproveSelected: PropTypes.func.isRequired,
  handleRejectSelected: PropTypes.func.isRequired,
  isRejecting: PropTypes.bool,
  messagesEndRef: PropTypes.object,
  handlePreview: PropTypes.func.isRequired,
};

export default ChatMessages;
